'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { submitAccessRequest } from '@/actions/access-requests';
import { REQUESTABLE_STAFF_TITLES } from '@/lib/permissionTypes';
import { Loader2 } from 'lucide-react';

// Same pattern as (auth)/login — no CAPTCHA gate at all before today, and a
// public, unauthenticated form that creates a database row is exactly the
// kind of thing that gets scraped/spammed (2026-08-22 security audit).
const TURNSTILE_SITEKEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? '';

export default function RequestAccess() {
  const router = useRouter();
  const [captchaToken, setCaptchaToken] = useState<string>();
  const captcha = useRef<TurnstileInstance>(null);

  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    preferredName: '',
    lastName: '',
    email: '',
    phone: '',
    requestingAs: 'student' as 'student' | 'staff',
    requestedTitle: '' as string,
  });

  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [redirectIn, setRedirectIn] = useState(20);
  const [fromGoogle, setFromGoogle] = useState(false);
  const [alreadyPending, setAlreadyPending] = useState(false);
  const [wasDeclined, setWasDeclined] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('from') !== 'google') return;
    setFromGoogle(true);
    setAlreadyPending(params.get('pending') === '1');
    setWasDeclined(params.get('declined') === '1');
    setFormData(prev => ({
      ...prev,
      firstName: params.get('firstName') ?? prev.firstName,
      lastName: params.get('lastName') ?? prev.lastName,
      email: params.get('email') ?? prev.email,
    }));
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (TURNSTILE_SITEKEY && !captchaToken) {
      setError('Complete the CAPTCHA challenge first.');
      return;
    }
    setLoading(true);

    const result = await submitAccessRequest({
      firstName: formData.firstName,
      middleName: formData.middleName,
      preferredName: formData.preferredName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      requestingAs: formData.requestingAs,
      requestedTitle: formData.requestingAs === 'staff' ? formData.requestedTitle : undefined,
      captchaToken,
    });

    setLoading(false);

    if ('error' in result) {
      // Only reset/re-challenge on a genuine failure — same lesson as
      // (auth)/login: resetting after success needlessly re-locks the button
      // right as navigation would occur, which reads as a required second click.
      captcha.current?.reset();
      setCaptchaToken(undefined);
      setError(result.error);
    } else {
      setSubmitted(true);
      setRedirectIn(20);
    }
  }

  useEffect(() => {
    if (!submitted) return;
    if (redirectIn <= 0) {
      router.push('/login');
      return;
    }
    const id = window.setTimeout(() => setRedirectIn(n => n - 1), 1000);
    return () => window.clearTimeout(id);
  }, [submitted, redirectIn, router]);

  if (submitted) {
    return (
      <div className="auth-backdrop min-h-screen flex items-center justify-center px-4 py-12">

        <div className="w-full max-w-md">
          <div className="bg-[var(--bg-surface)] rounded-2xl shadow-xl overflow-hidden">
            <div className="px-8 pt-10 pb-6" style={{ backgroundColor: '#121212' }}>
              <div className="flex flex-col items-center gap-3">
                <div className="text-5xl">✓</div>
                <div className="text-center">
                  <div className="text-white text-xl font-bold tracking-tight">Request received</div>
                  <div className="text-xs font-medium mt-0.5 text-[var(--text-2)]">We'll review your request soon</div>
                </div>
              </div>
            </div>
            <div className="px-8 py-8">
              <p className="text-sm text-[var(--text-2)] mb-6">
                Your access request has been submitted to the Lab Supervisor or Production Manager. You'll receive an email within 1-2 business days with next steps.
              </p>
              <p className="text-xs text-[var(--text-3)] mb-6">
                In the meantime, check your email for any follow-up questions about your request.
              </p>
              <p className="text-sm text-[var(--text-1)] font-medium text-center">
                Redirecting to the sign-in page in {redirectIn} second{redirectIn === 1 ? '' : 's'}.
              </p>
              <a
                href="/login"
                className="mt-3 block text-center text-xs font-medium hover:underline"
                style={{ color: 'var(--amber)' }}
              >
                Go to sign in now
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-backdrop min-h-screen flex items-center justify-center px-4 py-12">

      <div className="w-full max-w-md">
        <div className="bg-[var(--bg-surface)] rounded-2xl shadow-xl overflow-hidden">
          <div className="px-8 pt-10 pb-6" style={{ backgroundColor: '#121212' }}>
            <div className="flex flex-col items-center gap-3">
              <img src="/brand/puma-filmstrip-transparent.png" alt="" className="w-16 h-16 object-contain" />
              <div className="text-center">
                <div className="text-white text-xl font-bold tracking-tight">Bunker Box</div>
                <div className="text-xs font-medium mt-0.5" style={{ color: 'var(--amber)' }}>Equipment Management</div>
              </div>
            </div>
          </div>

          <div className="px-8 py-8">
            <p className="text-center text-sm text-[var(--text-2)] font-medium mb-6">Request access to the equipment library</p>

            {fromGoogle && alreadyPending && (
              <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-sm text-amber-900 dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-200">
                Google confirmed this email, and a request is already waiting for staff review. You can sign in with Google after they approve it.
              </div>
            )}
            {fromGoogle && !alreadyPending && (
              <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-sm text-amber-900 dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-200">
                {wasDeclined
                  ? 'A previous request for this Google email was declined. You can submit a new one below.'
                  : 'Google confirmed this email, but it is not on the staff or student list yet. Send a request — after approval, use Continue with Google to sign in.'}
              </div>
            )}

            {alreadyPending ? (
              <a
                href="/login"
                className="block w-full py-3 rounded-xl text-sm font-bold text-center"
                style={{ backgroundColor: 'var(--amber)', color: 'var(--primary-foreground)' }}
              >
                Back to sign in
              </a>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-[var(--text-2)] mb-1 uppercase tracking-wide">First name</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                    placeholder="Jon"
                    className="w-full border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm bg-[var(--bg-surface)] text-[var(--text-1)] placeholder:text-[var(--text-3)] focus:outline-none focus:ring-2 focus:ring-brand"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[var(--text-2)] mb-1 uppercase tracking-wide">Last name</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                    placeholder="Doe"
                    className="w-full border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm bg-[var(--bg-surface)] text-[var(--text-1)] placeholder:text-[var(--text-3)] focus:outline-none focus:ring-2 focus:ring-brand"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[var(--text-2)] mb-1 uppercase tracking-wide">Middle name <span className="font-normal text-[var(--text-3)]">(optional)</span></label>
                <input
                  type="text"
                  value={formData.middleName}
                  onChange={e => setFormData({ ...formData, middleName: e.target.value })}
                  className="w-full border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm bg-[var(--bg-surface)] text-[var(--text-1)] placeholder:text-[var(--text-3)] focus:outline-none focus:ring-2 focus:ring-brand"
                />
              </div>

              {formData.requestingAs === 'student' && (
                <div>
                  <label className="block text-xs font-semibold text-[var(--text-2)] mb-1 uppercase tracking-wide">
                    Preferred name <span className="font-normal text-[var(--text-3)]">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.preferredName}
                    onChange={e => setFormData({ ...formData, preferredName: e.target.value })}
                    className="w-full border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm bg-[var(--bg-surface)] text-[var(--text-1)] placeholder:text-[var(--text-3)] focus:outline-none focus:ring-2 focus:ring-brand"
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-[var(--text-2)] mb-1 uppercase tracking-wide">Email</label>
                <input
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    placeholder="you@email.com"
                    className="w-full border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm bg-[var(--bg-surface)] text-[var(--text-1)] placeholder:text-[var(--text-3)] focus:outline-none focus:ring-2 focus:ring-brand disabled:opacity-70"
                    required
                    readOnly={fromGoogle && !!formData.email}
                  />
                  {fromGoogle && formData.email && (
                    <p className="text-[11px] text-[var(--text-3)] mt-1">Locked to the Google account you just used.</p>
                  )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-[var(--text-2)] mb-1 uppercase tracking-wide">Phone <span className="font-normal text-[var(--text-3)]">(optional)</span></label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(407) 555-0110"
                  className="w-full border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm bg-[var(--bg-surface)] text-[var(--text-1)] placeholder:text-[var(--text-3)] focus:outline-none focus:ring-2 focus:ring-brand"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-[var(--text-2)] mb-2 uppercase tracking-wide">I'm requesting access as</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, requestingAs: 'student', requestedTitle: '' })}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                      formData.requestingAs === 'student'
                        ? 'text-brand-fg'
                        : 'border border-[var(--border)] text-[var(--text-1)] hover:bg-[var(--bg-raised)]'
                    }`}
                    style={formData.requestingAs === 'student' ? { backgroundColor: 'var(--amber)' } : {}}
                  >
                    Student
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, requestingAs: 'staff' })}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                      formData.requestingAs === 'staff'
                        ? 'text-brand-fg'
                        : 'border border-[var(--border)] text-[var(--text-1)] hover:bg-[var(--bg-raised)]'
                    }`}
                    style={formData.requestingAs === 'staff' ? { backgroundColor: 'var(--amber)' } : {}}
                  >
                    Staff
                  </button>
                </div>
              </div>

              {formData.requestingAs === 'staff' && (
                <div>
                  <label className="block text-xs font-semibold text-[var(--text-2)] mb-2 uppercase tracking-wide">Title</label>
                  <div className="grid grid-cols-1 gap-2">
                    {REQUESTABLE_STAFF_TITLES.map(title => (
                      <button
                        key={title}
                        type="button"
                        onClick={() => setFormData({ ...formData, requestedTitle: title })}
                        className={`py-2.5 px-3 rounded-lg text-sm font-semibold transition-all text-left ${
                          formData.requestedTitle === title
                            ? 'text-brand-fg'
                            : 'border border-[var(--border)] text-[var(--text-1)] hover:bg-[var(--bg-raised)]'
                        }`}
                        style={formData.requestedTitle === title ? { backgroundColor: 'var(--amber)' } : {}}
                      >
                        {title}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {TURNSTILE_SITEKEY ? (
                <div className="flex justify-center">
                  <Turnstile
                    ref={captcha}
                    siteKey={TURNSTILE_SITEKEY}
                    onSuccess={(token) => setCaptchaToken(token)}
                    onExpire={() => setCaptchaToken(undefined)}
                  />
                </div>
              ) : null}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-sm text-red-600 font-medium dark:bg-red-950 dark:border-red-800 dark:text-red-200">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || (!!TURNSTILE_SITEKEY && !captchaToken) || (formData.requestingAs === 'staff' && !formData.requestedTitle)}
                className="w-full py-3 rounded-xl text-sm font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: 'var(--amber)', color: 'var(--primary-foreground)' }}
              >
                {loading
                  ? <><Loader2 size={16} className="animate-spin" /> Submitting…</>
                  : (TURNSTILE_SITEKEY && !captchaToken)
                    ? <><Loader2 size={16} className="animate-spin" /> Verifying…</>
                    : 'Submit Request'}
              </button>
            </form>
            )}

            <p className="text-xs text-center text-[var(--text-3)] mt-6">
              Your request will be reviewed by the Lab Supervisor or Production Manager. If approved, you'll receive an email with a secure link to set your password.
            </p>
          </div>

          <div className="px-8 py-3 text-center bg-[var(--bg-raised)]">
            <a
              href="/login"
              className="text-xs font-medium hover:underline"
              style={{ color: 'var(--amber)' }}
            >
              Already have an account? Sign in
            </a>
            <p className="text-[10px] mt-1 text-[var(--text-3)]">Bunker Box</p>
          </div>
        </div>
      </div>
    </div>
  );
}
